import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import {
  formatCountryOptions,
  formatMobileCodeOptions,
  formatCurrencyOptions,
} from "../../lib/CountryDataFromatter";
import config from "../../config";

const getFlagUrl = (countryCode) =>
  `https://flagcdn.com/16x12/${countryCode.toLowerCase()}.png`;
// Base selector component
const BaseSelector = ({
  options,
  value,
  onChange,
  onBlur,
  name,
  renderOption,
  placeholder,
  error,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) || option.value.includes(searchTerm.toLowerCase()) || option.flag.includes(searchTerm.toLowerCase()) || option.dialCode.includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const selectedOption = options.find((opt) => {
    if (value === undefined || value === null || value === "") return false;
    return String(opt.value).toLowerCase() === String(value).toLowerCase();
  });

  return (
    <div className="relative ">
      <div
        className={` border rounded-full ${
          error ? "border-red-500" : "border-gray-300"
        } 
          ${disabled ? "bg-gray-100" : "bg-white"}`}
      >
        <div
          className="flex items-center p-2 cursor-pointer space-x-6"
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex items-center gap-2 flex-1">
            {value && selectedOption ? (
              renderOption(selectedOption)
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </div>
          <div className="text-gray-400 mx-2">
            <svg
              className={`w-4 h-4 transition-transform ${
                isOpen ? "transform rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M19 9l-7 7-7-7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </div>
        </div>

        {isOpen && (
          <div className="min-w-[200px] absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
            <div className="p-2 border-b">
              <div className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded">
                <Search className="w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  className="w-full bg-transparent border-none focus:outline-none text-sm"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="max-h-60 overflow-auto">
              {filteredOptions.map((option) => (
                <div
                  key={option.value+option.flag}
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  {renderOption(option)}
                </div>
              ))}
              {filteredOptions.length === 0 && (
                <div className="p-2 text-gray-400 text-center">
                  No results found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};

// Country Selector Component
export const CountrySelector = ({
  value,
  onChange,
  error,
  disabled,
  name,
  onBlur,
}) => {
  const countryOptions = useMemo(() => formatCountryOptions(), []);
  return (
    <BaseSelector
      options={countryOptions}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      name={name}
      error={error}
      disabled={disabled}
      placeholder="Select country"
      renderOption={(option) => (
        <div className="flex items-center gap-2">
          <img
            src={getFlagUrl(option.flag)}
            alt={`flag of ${option.flag}`}
            style={{ width: "16px", height: "auto" }}
          />
          <span>{option.label}</span>
        </div>
      )}
    />
  );
};

// Mobile Code Selector Component
export const MobileCodeSelector = ({
  value,
  onChange,
  error,
  disabled,
  name,
  onBlur,
}) => {
  const mobileCodeOptions = useMemo(() => formatMobileCodeOptions(), []);

  useEffect(() => {
    if (value || disabled || typeof onChange !== "function") return;

    const lebanonOption = mobileCodeOptions.find((option) =>
      String(option.value) === "961"
    );

    if (lebanonOption) {
      onChange(lebanonOption.value);
    }
  }, [value, disabled, onChange, mobileCodeOptions]);

  return (
    <BaseSelector
      options={mobileCodeOptions}
      value={value }
      onChange={onChange}
      onBlur={onBlur}
      name={name}
      error={error}
      disabled={disabled}
       placeholder="Code"
      renderOption={(option) => (
        <div className="flex items-center gap-2">
          <img
            src={getFlagUrl(option.flag)}
            alt={`flag of ${option.flag}`}
            style={{ width: "16px", height: "auto" }}
          />
          <span>{option.dialCode}</span>
        </div>
      )}
    />
  );
};

// Currency Selector Component
export const CurrencySelector = ({
  value,
  onChange,
  error,
  disabled,
  name,
  onBlur,
}) => {
  const currencyOptions = useMemo(() => formatCurrencyOptions(), []);

  return (
    <BaseSelector
      options={currencyOptions}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      name={name}
      error={error}
      disabled={disabled}
      placeholder="Select currency"
      renderOption={(option) => (
        <div className="flex items-center gap-2">
          <img
            src={getFlagUrl(option.flag)}
            alt={`flag of ${option.flag}`}
            style={{ width: "16px", height: "auto" }}
          />
          <span>{option.value}</span>
        </div>
      )}
    />
  );
};

// Example usage with react-hook-form
export default function Example() {
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedMobileCode, setSelectedMobileCode] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");

  return (
    <div className="space-y-4 p-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Country
        </label>
        <CountrySelector
          value={selectedCountry}
          onChange={setSelectedCountry}
          error={null}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Mobile Code
        </label>
        <MobileCodeSelector
          value={selectedMobileCode}
          onChange={setSelectedMobileCode}
          error={null}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Currency
        </label>
        <CurrencySelector
          value={selectedCurrency}
          onChange={setSelectedCurrency}
          error={null}
        />
      </div>
    </div>
  );
}
